<template>
  <div
    v-click-outside="clickOutSideRubricSwitcher"
    class="wrapper"
  >
    <div
      class="rubric-switcher"
      :class="{'rubric-switcher--active': isShowRubricSwitcher}"
      :style="`width: ${newsPageRoofWidth >= 1400 ? 219 : 122}px;`"
      @click="isShowRubricSwitcher = !isShowRubricSwitcher"
    >
      <span
        class="rubric-switcher__title"
        :class="{'rubric-switcher__title--active': isShowRubricSwitcher}"
      >
        {{ t('news_page_rubrics_switcher.label.rubric') }}
        <template v-if="typeof rubricDisplayText === 'number'">
          ({{ rubricDisplayText }})
        </template>
      </span>
      <span
        v-if="typeof rubricDisplayText === 'string'"
        class="rubric-switcher__name"
      >
        {{ rubricDisplayText }}
      </span>
      <ArrowBottomIcon
        class="rubric-switcher__icon"
        :class="{'rubric-switcher__icon--active': isShowRubricSwitcher}"
      />
    </div>

    <div
      v-if="isShowRubricSwitcher"
      class="rubric-switcher__list"
    >
      <NewsPageRubricsList
        v-if="!!rubricsStore.items?.length && !rubricsStore.rubricLoader"
        :active-rubric="activeRubric"
        @set-active-rubric="rubric => setActiveRubric(rubric)"
        @open-delete-popup="(val) => openDeletePopup(val)"
        @open-edit-popup="openEditPopup"
      />
      <div
        v-if="rubricsStore.rubricLoader"
        class="rubric-switcher__loader"
      >
        <LocalLoader />
      </div>
      <div
        v-if="rubricsStore.items?.length === 0 && !rubricsStore.rubricLoader"
        class="rubric-switcher__empty"
      >
        {{ t('news_page_rubrics_switcher.label.not_found') }}.
      </div>
      <ButtonAccentPrimary
        v-if="LKStore.user.roleNames.includes('аналитик') || LKStore.project.isOwner"
        class="rubric-switcher__button"
        @click.stop="setActiveRubric(null)"
      >
        {{ t('news_page_rubrics_switcher.label.rubric_new') }}
      </ButtonAccentPrimary>
    </div>

    <NewsPageRubricsEditPopup
      v-if="isShowRubricEditPopup"
      :rubric="activeRubric"
      :is-new-rubric="isNewRubric"
      @open-delete-popup="(val) => openDeletePopup(val)"
      @close-edit-popup="closeEditPopup"
    />

    <Modal
      v-if="isShowRubricDeletePopup"
      teleport="#app"
      :title="t('news_page_popup_delete.title.delete_rubric')"
      :confirm-text="t('news_page_popup_delete.button.accept_delete')"
      :cancel-text="t('news_page_popup_delete.button.cancel_button')"
      :is-confirm-pressed="isPressedDeletePopupButton"
      @confirm="deleteRubric(activeRubric.id)"
      @cancel="handleDeleteModalClose"
    >
      <template v-if="isLastRubricInTheme && lastRubricInThemeList.length">
        {{ t('news_page_popup_delete.message.delete_last_rubric') }}
        {{ formatThemeNames(lastRubricInThemeList) }}.
        {{ t('news_page_popup_delete.message.delete_last_rubric_second') }}
      </template>
      <template v-else>
        {{ t('news_page_popup_delete.message.delete_rubric') }}
      </template>
    </Modal>
  </div>
</template>

<script setup>
import { useRubricsStore } from "@/store/NewsFeed/rubrics";
import { defineProps, ref, computed } from "vue";
import { useNewsFeedStore } from "@/store/NewsFeed/newsFeed";
import { useNotificationsStore } from "@/store/notifications";
import { useLK } from "@/controllerLK/LKstore";
import NewsPageRubricsList from "./NewsPageRubricsList.vue";
import NewsPageRubricsEditPopup from "@/components/NewsPage/NewsPagePopup/NewsPageRubricsEditPopup.vue";
import LocalLoader from "@/components/UI/LocalLoader.vue";
import { t } from "@/controllerLK/AvalLocalization";
import ArrowBottomIcon from "@/components/Icons/ArrowBottomIcon.vue";
import { useBlockStore } from "@/store/NewsFeed/blocks";

const props = defineProps({
  newsPageRoofWidth: {
    type: Number,
    required: true
  }
});

// store
const LKStore = useLK();
const newsFeedStore = useNewsFeedStore();
const blockStore = useBlockStore();
const rubricsStore = useRubricsStore();
const notificationsStore = useNotificationsStore();

// Отображение модального окна редактирования рубрики
const isShowRubricEditPopup = ref(false);

// Отображение модального окна удаления рубрики
const isShowRubricDeletePopup = ref(false);

// Отображение выпадающего списка выбора рубрик
const isShowRubricSwitcher = ref(false);

// Флаг отправки рубрики на удаление.
const isPressedDeletePopupButton = ref(false);

// Флаг создания новой рубрики или редактирования старой
const isNewRubric = ref(false);

// Активная рубрика для редактирования/создания
const activeRubric = ref(null);

// Обработка события клика вне переключателя рубрик
const clickOutSideRubricSwitcher = () => {
  if (!isShowRubricDeletePopup.value && !isShowRubricEditPopup.value) {
    isShowRubricSwitcher.value = false;
    isLastRubricInTheme.value = false;
    lastRubricInThemeList.value = [];
  }
};

// Обработка события закрытия окна удаления рубрики
const handleDeleteModalClose = () => {
  isShowRubricDeletePopup.value = false;
  isLastRubricInTheme.value = false;
  lastRubricInThemeList.value = [];
};

// Установка активной рубрики для редактирования/создания
const setActiveRubric = (rubric) => {
  if (rubric) {
    activeRubric.value = rubric;
    isNewRubric.value = false;
  } else {
    activeRubric.value = newRubric;
    isNewRubric.value = true;
    openEditPopup();
  }
};

// Событие открытия модального окна удаления рубрики
const openDeletePopup = (rubric) => {
  isLastRubricInThemeCheck(rubric);
  isShowRubricDeletePopup.value = true;
};

// Является ли рубрика последней в теме.
const lastRubricInThemeList = ref([]);
const isLastRubricInTheme = ref(false);
const isLastRubricInThemeCheck = (rubric) => {
  for (const theme of blockStore.allThemes) {
    if (theme.rubric_ids.length === 1 && theme.rubric_ids[0] === rubric.id) {
      isLastRubricInTheme.value = true;
      lastRubricInThemeList.value.push(theme);
    }
  }
};

// Функция удаления рубрики.
async function deleteRubric(item) {
  isPressedDeletePopupButton.value = true;

  try {
    await rubricsStore.delRubric(item);
    await blockStore.getAllThemes();

    if (!rubricsStore.chosenRubrics.length) {
      rubricsStore.chosenRubrics = [];
    }

    newsFeedStore.clearNewsFeed();
    notificationsStore.createNotify(t("news_page_popup_delete.message.delete_rubric_confirmed"), "success");
    isShowRubricDeletePopup.value = false;
    isShowRubricEditPopup.value = false;
    isPressedDeletePopupButton.value = false;

  } catch (e) {
    console.log(e);
  }
}

// Начальное состояние рубрики
const newRubric = {
  name: "",
  description: "",
  query: "",
  is_enabled: true,
  is_fixed: false,
  type: 0
};

// Событие открытие модального окна редактирования рубрики
const openEditPopup = () => {
  isShowRubricEditPopup.value = true;
};

// Событие закрытия модального окна редактирования рубрики
const closeEditPopup = () => {
  activeRubric.value = null;
  isShowRubricSwitcher.value = false;
  isShowRubricEditPopup.value = false;
};

// Форматирование название рубрик для модального окна удаления
const formatThemeNames = (themes) => {
  return themes.map(theme => theme.name).join(", ");
};

// Форматирование текста в поле выбора рубрики в зависимости от количества выбранных рубрик.
const rubricDisplayText = computed(() => {
  if (props.newsPageRoofWidth >= 1400) {
    if (rubricsStore.chosenRubrics.length === 0) {
      return t("news_page_rubrics_switcher.label.rubric_not_selected");
    }

    if (rubricsStore.isAllRubricsChosen() && rubricsStore.chosenRubrics.length > 0) {
      return t("news_page_rubrics_switcher.label.rubric_all");
    }

    if (!rubricsStore.isAllRubricsChosen() && rubricsStore.chosenRubrics.length === 1) {
      return rubricsStore.allRubrics.find((rubric) => rubricsStore.chosenRubrics.includes(rubric.id))?.name;
    }

    if (!rubricsStore.isAllRubricsChosen() && rubricsStore.chosenRubrics.length > 1) {
      return rubricsStore.allRubrics.filter((rubric) => rubricsStore.chosenRubrics.includes(rubric.id)).length;
    }

    return "";
  } else {

    if (rubricsStore.isAllRubricsChosen() && rubricsStore.chosenRubrics.length > 0) {
      return t("news_page_rubrics_switcher.label.rubric_all");
    } else {
      return rubricsStore.chosenRubrics.length;
    }
  }
});

</script>

<style lang="scss">
.wrapper {
  position: relative;
}

.rubric-switcher {
  background: var(--background-bg-white);
  display: flex;
  border-radius: 8px;
  justify-content: space-between;
  padding: 0 12px;
  cursor: pointer;
  height: 100%;
  align-items: center;
  gap: 4px;

  &--active {
    background: var(--background-bg-hover2);
  }

  &:hover {
    background: var(--background-bg-hover2);
  }

  &__button {
    width: 100%;
  }

  &__empty {
    width: 100%;
    height: 140px;
    background: var(--background-01);
    display: flex;
    justify-content: center;
    align-items: center;
    border-radius: 10px;
    font-size: 14px;
    line-height: 20px;
    text-align: center;
    padding: 30px 20px;

    p {
      font-size: 14px;
      line-height: 20px;
      text-align: center;
    }
  }

  &__loader {
    width: 200px;
    height: 140px;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  &__list {
    padding: 14px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    border-radius: 8px;
    position: absolute;
    top: 110%;
    left: 0;
    max-width: 100%;
    min-width: 320px;
    background: var(--background-bg-white);
    z-index: 5;
    box-shadow: 1px 2px 10px 0 #131e2726;
    gap: 10px;
  }

  &__icon {
    min-width: 17px;
    min-height: 17px;
    margin-left: auto;
    color: var(--icon-icon-secondary);

    &--active {
      rotate: 180deg;
      color: var(--icon-icon-on);
    }
  }

  &__title {
    color: var(--text-text-main);
    line-height: 18px;
    font-weight: 400;
    font-size: 14px;
    white-space: nowrap;

    &--active {
      color: var(--text-text-on);
    }
  }

  &__name {
    white-space: nowrap;
    line-clamp: 1;
    -webkit-line-clamp: 1;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
    color: var(--text-text-pur);
  }
}
</style>